import React, { useEffect, useMemo, useState } from "react";
import { fetchData } from "../../services/apiService";
import useAlertUtils from "../../utils/common.utils";
import "./UserAssessments.scss";
import Footer from "../../components/Footer/Footer";
import Header from "../../components/Header/Header";

function getUserIdFromToken() {
  try {
    const t =
      typeof window !== "undefined" ? localStorage.getItem("token") : null;
    if (!t) return null;
    const parts = t.split(".");
    if (parts.length < 2) return null;
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return payload.userId || payload.sub || payload.id || null;
  } catch (e) {
    return null;
  }
}

export default function UserAssessments() {
  const { alertError } = useAlertUtils();
  const [isLoading, setIsLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);

  const userId = useMemo(() => getUserIdFromToken(), []);

  useEffect(() => {
    let isMounted = true;
    async function run() {
      if (!userId) {
        setIsLoading(false);
        return;
      }
      try {
        const res = await fetchData(
          `/api/assessments/get-all-assessments/${userId}`
        );
        if (isMounted) {
          if (res && (res.success === true || res.status === true)) {
            setItems(Array.isArray(res.data) ? res.data : []);
          } else if (Array.isArray(res)) {
            setItems(res);
          } else {
            setItems([]);
            alertError((res && res.message) || "Failed to load assessments");
          }
        }
      } catch (e) {
        if (isMounted) {
          setItems([]);
          try {
            alertError("Failed to load assessments");
          } catch (_) {}
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }
    run();
    return () => {
      isMounted = false;
    };
  }, [userId]);

  const prettyDate = (iso) => {
    if (!iso) return "-";
    try {
      return new Date(iso).toLocaleDateString();
    } catch {
      return iso;
    }
  };

  const parseMaybeJSON = (value) => {
    try {
      let out = value;
      while (typeof out === "string") {
        out = JSON.parse(out);
      }
      return out;
    } catch (_) {
      return value;
    }
  };

  const capitalizeFirst = (s) => {
    try {
      if (typeof s !== "string" || s.length === 0) return s;
      return s.charAt(0).toUpperCase() + s.slice(1);
    } catch (_) {
      return s;
    }
  };

  const allEntries = useMemo(() => {
    return (items || []).map((row) => {
      const entryDate =
        row.entryDate || row.date || row.createdAt || row.created_at;
      const genderAtEntry =
        row.genderAtEntry || row.gender || row.gender_at_entry;
      const totalScore = row.totalScore ?? row.total ?? row.total_score;
      const maxTotal = row.maxTotal ?? row.max_total ?? row.max;
      const ratingLabel = row.ratingLabel || row.rating || row.rating_label;
      const id = row.id || `${entryDate}-${totalScore}-${maxTotal}`;
      const domainScoresParsed = (() => {
        try {
          const raw = row.domainScores ?? row.domain_scores;
          const parsed = parseMaybeJSON(raw);
          return parsed && typeof parsed === "object" ? parsed : {};
        } catch {
          return {};
        }
      })();
      const answersParsed = (() => {
        try {
          const rawAns = row.answers ?? row.answers_json ?? row.answersJson;
          const parsedAns = parseMaybeJSON(rawAns);
          return parsedAns && typeof parsedAns === "object" ? parsedAns : {};
        } catch {
          return {};
        }
      })();
      return {
        id,
        entryDate,
        genderAtEntry,
        totalScore,
        maxTotal,
        ratingLabel,
        domainScores: domainScoresParsed,
        answers: answersParsed,
      };
    });
  }, [items]);

  useEffect(() => {
    if (allEntries.length > 0) {
      const sorted = [...allEntries].sort(
        (a, b) => new Date(b.entryDate) - new Date(a.entryDate)
      );
      const newest = sorted[0];
      if (!selectedEntry || selectedEntry.id !== newest.id) {
        setSelectedEntry(newest);
      }
    }
  }, [allEntries]);

  const todayStr = useMemo(() => {
    try {
      return new Date().toISOString().slice(0, 10);
    } catch {
      return "";
    }
  }, []);
  const hasToday = useMemo(
    () => allEntries.some((e) => (e.entryDate || "").slice(0, 10) === todayStr),
    [allEntries, todayStr]
  );

  const sortedEntries = useMemo(
    () =>
      [...allEntries].sort(
        (a, b) => new Date(b.entryDate).getTime() - new Date(a.entryDate).getTime()
      ),
    [allEntries]
  );

  const buttonEntries = useMemo(() => {
    if (!hasToday) {
      return [
        { id: "add-today", special: true },
        ...sortedEntries,
      ];
    }
    return sortedEntries;
  }, [hasToday, sortedEntries]);

  return (
    <div>
      <Header />
      <div className="ua-container">
        <div className="ua-card">
          <div className="ua-header">
            <h1 className="ua-title">Symptoms Tracker History</h1>
            <p className="ua-subtitle">
              View and analyze all your previous assessment entries over time.
            </p>
          </div>

          {isLoading ? (
            <div className="ua-loading">
              <span className="ua-spinner" aria-hidden="true"></span>
              Loading assessments...
            </div>
          ) : allEntries.length === 0 ? (
            <div className="ua-empty">
              <div className="ua-empty__icon" aria-hidden="true">
                📄
              </div>
              <div className="ua-empty__title">No assessments found</div>
              <div className="ua-empty__text">
                Your saved assessments will appear here after you submit.
              </div>
              <div style={{ marginTop: 20 }}>
                <button
                  className="ua-chip-btn ua-chip-btn--primary"
                  onClick={() => { window.location.href = "/app/symptoms-tracker"; }}
                  title="Start a new assessment"
                >
                  <span className="ua-chip-btn__icon">+</span>
                  Add Your First Symptoms
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="ua-datepick">
                <div className="ua-datepick__row">
                  <div className="ua-datepick__label">Select Date</div>
                  <div className="ua-datepick__buttons">
                    {buttonEntries.map((entry) =>
                      entry.special ? (
                        <button
                          key="add-today"
                          className="ua-chip-btn ua-chip-btn--primary"
                          onClick={() => {
                            window.location.href = "/app/symptoms-tracker";
                          }}
                          title="Start a new assessment for today"
                        >
                          <span className="ua-chip-btn__icon">+</span>
                          Add Today's Symptoms
                        </button>
                      ) : (
                      <button
                        key={entry.id}
                        onClick={() => setSelectedEntry(entry)}
                        className={`ua-chip-btn ${
                          selectedEntry && selectedEntry.id === entry.id
                            ? "is-active"
                            : ""
                        }`}
                      >
                        {entry.entryDate}
                      </button>
                      )
                    )}
                  </div>
                </div>
              </div>

              {selectedEntry && (
                <>
                  <div className="ua-summary">
                    <div className="ua-summary__card">
                      <div className="ua-summary__label">Total Score</div>
                      <div className="ua-summary__value">
                        {selectedEntry.totalScore}{" "}
                        <span className="ua-summary__max">
                          / {selectedEntry.maxTotal}
                        </span>
                      </div>
                    </div>
                    <div className="ua-summary__card">
                      <div className="ua-summary__label">Status</div>
                      <div className="ua-summary__value">
                        {selectedEntry.ratingLabel}
                      </div>
                    </div>
                    <div className="ua-summary__card">
                      <div className="ua-summary__label">Gender</div>
                      <div className="ua-summary__value ua-capitalize">
                        {selectedEntry.genderAtEntry}
                      </div>
                    </div>
                  </div>

                  <div className="ua-panel2">
                    <div className="ua-panel2__title">Domain Scores</div>
                    <div className="ua-domains">
                      {Object.entries(selectedEntry.domainScores || {}).map(
                        ([domain, score]) => {
                          const value = (score && score.value) ?? 0;
                          const max = (score && score.max) ?? 1;
                          const percentage = Math.min(
                            100,
                            Math.max(0, (value / max) * 100)
                          );
                          return (
                            <div key={domain} className="ua-domain">
                              <div className="ua-domain__row">
                                <span className="ua-domain__label">
                                  {domain}
                                </span>
                                <span className="ua-domain__value">
                                  {value} / {max}
                                </span>
                              </div>
                              <div className="ua-domain__track">
                                <div
                                  className={`ua-domain__fill ${
                                    percentage > 50
                                      ? "is-high"
                                      : percentage > 25
                                      ? "is-mid"
                                      : "is-low"
                                  }`}
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>

                  <div className="ua-answers">
                    <details className="ua-details">
                      <summary className="ua-details__summary">Answers</summary>
                      <div className="ua-ansgrid">
                        {Object.entries(selectedEntry.answers || {}).map(
                          ([k, v]) => (
                            <div className="ua-anscard" key={k}>
                              <div className="ua-anscard__key">
                                {capitalizeFirst(k)}
                              </div>
                              <div className="ua-anscard__val">{String(v)}</div>
                            </div>
                          )
                        )}
                      </div>
                    </details>
                  </div>
                </>
              )}

              <p className="ua-footer">
                © {new Date().getFullYear()} MedHealth Systems — Symptom Tracker
              </p>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
